import { Question, isQuestionContent } from "@/types/test";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Code, Play, Clock, HardDrive } from "lucide-react";

interface CodingQuestionViewProps {
  question: Question;
}

export const CodingQuestionView = ({ question }: CodingQuestionViewProps) => {
  const questionContent = isQuestionContent(question.content) ? question.content : { question: '' };
  const codeTests = question.code_tests;

  if (!codeTests) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Coding question configuration not found.</p>
        </CardContent>
      </Card>
    );
  }

  const visibleTestCases = codeTests.test_cases.filter(tc => !tc.hidden);
  const hiddenTestCases = codeTests.test_cases.filter(tc => tc.hidden);

  return (
    <div className="space-y-6">
      {/* Question Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Coding Question
            <Badge variant="secondary" className="ml-auto">
              {codeTests.language.charAt(0).toUpperCase() + codeTests.language.slice(1)}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose max-w-none">
            <div className="whitespace-pre-wrap">{questionContent.question}</div>
          </div>
          
          {questionContent.starterCode && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Starter Code:</h4>
              <pre className="bg-muted p-3 rounded-md text-sm font-mono overflow-x-auto">
                <code>{questionContent.starterCode}</code>
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Constraints */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Execution Constraints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Time Limit: <strong>{codeTests.constraints.time_limit_seconds}s</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Memory Limit: <strong>{codeTests.constraints.memory_limit_mb}MB</strong>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Cases */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Test Cases
            <Badge variant="outline" className="ml-auto">
              {codeTests.test_cases.length} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {visibleTestCases.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  Public Test Cases
                  <Badge variant="default">{visibleTestCases.length}</Badge>
                </h4>
                <div className="space-y-3">
                  {visibleTestCases.map((testCase, index) => (
                    <Card key={index} className="border-border/50">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium mb-2">Input:</h5>
                            <pre className="bg-muted p-2 rounded text-sm font-mono whitespace-pre-wrap">
                              {testCase.input || "(no input)"}
                            </pre>
                          </div>
                          <div>
                            <h5 className="font-medium mb-2">Expected Output:</h5>
                            <pre className="bg-muted p-2 rounded text-sm font-mono whitespace-pre-wrap">
                              {testCase.expected_output}
                            </pre>
                          </div>
                        </div>
                        {testCase.weight > 1 && (
                          <div className="mt-2">
                            <Badge variant="outline">Weight: {testCase.weight}</Badge>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {hiddenTestCases.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  Hidden Test Cases
                  <Badge variant="secondary">{hiddenTestCases.length}</Badge>
                </h4>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-sm">
                      {hiddenTestCases.length} hidden test case{hiddenTestCases.length !== 1 ? 's' : ''} 
                      will be used to evaluate your solution. These test cases are not visible to candidates.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};